/**
 * 前端TOTP生成器（兼容浏览器）
 * 支持Google Authenticator兼容的2FA动态码生成
 */
class TOTPGenerator {
  
  // 生成6位TOTP动态码
  static async generate(secret, window = 0) {
    try {
      // 1. 解码Base32密钥
      const key = this.base32ToBytes(secret);
      
      // 2. 计算时间窗口（30秒一个）
      const time = Math.floor(Date.now() / 1000 / 30) + window;
      
      // 3. 时间转8字节数组
      const timeBytes = this.intToBytes(time);
      
      // 4. 使用Web Crypto API计算HMAC-SHA1
      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        key,
        { name: 'HMAC', hash: 'SHA-1' },
        false,
        ['sign']
      );
      
      const signature = await crypto.subtle.sign(
        'HMAC',
        cryptoKey,
        timeBytes
      );
      
      // 5. 处理结果
      const hash = new Uint8Array(signature);
      const code = this.dynamicTruncation(hash);
      
      return code.toString().padStart(6, '0');
    } catch (error) {
      console.error('TOTP生成失败:', error);
      throw error;
    }
  }

  // 整数转8字节数组（大端序）
  static intToBytes(number) {
    const bytes = new Uint8Array(8);
    for (let i = 7; i >= 0; i--) {
      bytes[i] = number & 0xff;
      number = Math.floor(number / 256);
    }
    return bytes;
  }

  // Base32解码
  static base32ToBytes(base32) {
    const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    const base32Clean = base32.replace(/=+$/, '').toUpperCase();
    
    let buffer = 0;
    let bitsLeft = 0;
    const bytes = [];
    
    for (let i = 0; i < base32Clean.length; i++) {
      const char = base32Clean.charAt(i);
      const value = ALPHABET.indexOf(char);
      if (value === -1) continue;
      
      buffer = (buffer << 5) | value;
      bitsLeft += 5;
      
      if (bitsLeft >= 8) {
        bitsLeft -= 8;
        bytes.push((buffer >> bitsLeft) & 0xff);
      }
    }
    
    return new Uint8Array(bytes);
  }

  // 动态截取（RFC 4226标准）
  static dynamicTruncation(hash) {
    const offset = hash[hash.length - 1] & 0x0f;
    
    // 提取4字节
    const binaryCode = 
      ((hash[offset] & 0x7f) << 24) |
      ((hash[offset + 1] & 0xff) << 16) |
      ((hash[offset + 2] & 0xff) << 8) |
      (hash[offset + 3] & 0xff);
    
    // 取模生成6位数
    return binaryCode % 1000000;
  }

  // 验证TOTP码
  static async verify(token, secret, window = 1) {
    for (let i = -window; i <= window; i++) {
      const generated = await this.generate(secret, i);
      if (generated === token) {
        return true;
      }
    }
    return false;
  }

  // 生成随机密钥（Base32格式）
  static generateSecret(length = 20) {
    const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let result = '';
    
    // 使用浏览器安全的随机数生成器
    const randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);
    
    for (let i = 0; i < length; i++) {
      // 取5位（32个可能值）
      const value = randomValues[i] & 0x1f;
      result += ALPHABET.charAt(value);
    }
    
    // 添加填充
    while (result.length % 8 !== 0) {
      result += '=';
    }
    
    return result;
  }

  // 生成OTPAuth URL（用于生成二维码）
  static generateOTPAuthURL(secret, account, issuer = 'MyApp') {
    const encodedIssuer = encodeURIComponent(issuer);
    const encodedAccount = encodeURIComponent(account);
    return `otpauth://totp/${encodedIssuer}:${encodedAccount}?secret=${secret}&issuer=${encodedIssuer}&algorithm=SHA1&digits=6&period=30`;
  }

  // 获取剩余时间（秒）
  static getRemainingTime() {
    const now = Math.floor(Date.now() / 1000);
    return 30 - (now % 30);
  }
}

export { TOTPGenerator };
